// Shared markdown normalization for AI-generated answers, wherever they're
// rendered (ChatBlob's web-search answers, ContentRenderer's documents-mode
// answers, or anywhere else). The model's own markdown formatting is
// inconsistent response-to-response — these fixes are deliberately generic
// (structural detection / indentation math, never hardcoded to specific
// words like "Pros"/"Cons") so they apply to whatever an AI text throws at
// them, not just the exact cases seen so far. Used together with the
// `.md-ol-counter` CSS rules in index.css.

// The model sometimes writes a section header as "**1. Monday.com**" — the
// number sits INSIDE the bold markers, so CommonMark never parses it as a
// real ordered-list item (it's just a bold text run); whatever "- " bullets
// follow are therefore plain, un-nested, top-level bullets with nothing to
// attach to, not children of "1." — this is why every point renders at the
// same flat level regardless of which numbered section it belongs to.
// Rewrites "**N. Title**" into "N. **Title**" (number outside the bold, so
// it becomes a genuine list marker) and indents everything that follows —
// sub-labels and their bullets — by 4 spaces, up until the next such header
// or a "---" divider, so it nests under the right numbered item instead of
// floating as sibling content.
export function normalizePseudoNumberedHeaders(text) {
    const headerRe = /^\*\*(\d+)\.\s+(.+?)\*\*\s*$/;
    const hrRe = /^\s*-{3,}\s*$/;
    let inSection = false;
    return text.split('\n').map((line) => {
        const m = line.match(headerRe);
        if (m) {
            inSection = true;
            return `${m[1]}. **${m[2]}**`;
        }
        if (hrRe.test(line)) {
            inSection = false;
            return line;
        }
        if (inSection && line.trim() !== '') {
            return `    ${line}`;
        }
        return line;
    }).join('\n');
}

// The model often writes a sub-bullet with only 1-3 leading spaces (e.g.
// "- Parent\n - Child"), relying on visual indentation alone — but CommonMark
// requires at least 4 spaces (or the parent marker's own content column) to
// recognize a line as a NESTED list item; anything less gets parsed as a
// separate item at the SAME level as its "parent", which is why sub-points
// render flat instead of indented.
//
// Bumping only the too-shallow (1-3 space) lines to a flat 4 spaces — an
// earlier version of this function did exactly that — silently BREAKS
// already-correctly-nested deeper content: e.g. a real documents-mode
// answer had "   - Local supervisors...:" (3 spaces) with its own children
// "     - Safely stopping..." (5 spaces, genuinely nested, 2 more than its
// parent). Bumping only the 3-space parent to 4 left the child at its
// original 5 — now only 1 space deeper than its (moved) parent, no longer
// enough to register as nested, so it popped out to become a flat sibling.
// Instead, this collects every distinct indentation level actually used by
// bullet/numbered lines and remaps them, IN THE SAME RELATIVE ORDER, to a
// clean 0/4/8/12... ladder — so whatever relative nesting the model
// intended (2 spaces deeper here, 5 spaces deeper there) is always
// preserved, regardless of how few or many spaces it originally used at
// each level, while every level still ends up unambiguously deep enough
// for CommonMark to recognize it as nested under the one before it.
// Skips fenced code blocks so indentation inside actual code samples is
// never touched.
export function normalizeNestedBulletIndentation(text) {
    const segments = text.split(/(```[\s\S]*?```)/);
    return segments
        .map((seg, i) => (i % 2 === 1 ? seg : normalizeIndentLevels(seg)))
        .join('');
}

function normalizeIndentLevels(text) {
    const bulletRe = /^( *)([-*+]|\d+\.)(\s+)/;
    const lines = text.split('\n');

    const levels = new Set();
    for (const line of lines) {
        const m = line.match(bulletRe);
        if (m) levels.add(m[1].length);
    }
    if (levels.size === 0) return text;

    const sortedLevels = [...levels].sort((a, b) => a - b);
    const levelMap = new Map(sortedLevels.map((lvl, idx) => [lvl, idx * 4]));

    return lines.map((line) => {
        const m = line.match(bulletRe);
        if (!m) return line;
        const newIndent = levelMap.get(m[1].length);
        return ' '.repeat(newIndent) + line.slice(m[1].length);
    }).join('\n');
}

// Runs both text-level normalizations above, in the order that matters
// (pseudo-headers must become real list markers before indentation gets
// evaluated relative to them).
export function normalizeAiMarkdownText(text) {
    return normalizeNestedBulletIndentation(normalizePseudoNumberedHeaders(text));
}

// Structural (AST-level, not text-pattern) fix: in any list, a "label-only"
// item — one whose ENTIRE content is a single bold run, e.g. "- **Pros**",
// nothing else — is never a real peer of the bullets around it; it's a
// sub-heading for whichever bullets follow. remark/CommonMark has no idea of
// that intent though, so it parses all of them as flat siblings in one list,
// which is why every point renders at the same level regardless of which
// label it conceptually belongs to.
// This walks the parsed tree (works for ANY label text — "Pros", "Cons",
// "Type", whatever the model writes — since detection is structural, not a
// hardcoded word match) and physically moves every item following a label,
// up to the next label or the end of the list, INTO that label's own
// children as a real nested sub-list — so the resulting indentation is
// genuine tree structure, not just a CSS trick on the label itself.
export function remarkNestLabeledListItems() {
    return (tree) => walk(tree);

    function isLabelOnlyItem(item) {
        return !!(
            item.children &&
            item.children.length === 1 &&
            item.children[0].type === 'paragraph' &&
            item.children[0].children &&
            item.children[0].children.length === 1 &&
            item.children[0].children[0].type === 'strong'
        );
    }

    function regroupList(list) {
        const regrouped = [];
        let currentLabel = null;
        let buffer = [];
        const flush = () => {
            if (currentLabel && buffer.length > 0) {
                currentLabel.children.push({ type: 'list', ordered: list.ordered, start: null, spread: false, children: buffer });
            }
            buffer = [];
        };
        for (const item of list.children) {
            if (isLabelOnlyItem(item)) {
                flush();
                currentLabel = item;
                regrouped.push(item);
            } else if (currentLabel) {
                buffer.push(item);
            } else {
                regrouped.push(item);
            }
        }
        flush();
        list.children = regrouped;
    }

    function walk(node) {
        if (node.type === 'list') regroupList(node);
        if (node.children) node.children.forEach(walk);
    }
}

// Used by the `li` component override in both renderers to decide whether a
// hast <li> node is one of the label-only items above (post-transform, so
// it may now carry a nested sub-list as a second child alongside the
// <strong>) — hides its bullet and styles it as a sub-heading instead of
// another same-level point.
export function isLabelOnlyLiNode(node) {
    // A label-only <li> has nothing besides its bold run — optionally
    // followed by the nested <ul>/<ol> remarkNestLabeledListItems attaches
    // for the bullets that belong under it. Without this check, a tight list
    // (no blank lines between items — the normal case) unwraps its <p>
    // wrapper entirely, so an item like "- **Wayne County**: [link]" (bold
    // label PLUS trailing content on the same line) has hast children
    // [strong, text, a, ...] — `first?.tagName === 'strong'` alone matched
    // that too, wrongly rendering the whole line bold/bulletless.
    // Whitespace-only text nodes (bare "\n") are filtered out first — mdast-
    // to-hast inserts these around a nested list injected by
    // remarkNestLabeledListItems (it has no spread/blank-line metadata of
    // its own), so a genuine "label + nested sub-list" item would otherwise
    // fail the exact-length check purely because of that formatting noise.
    const isWhitespaceText = (n) => n?.type === 'text' && !n.value?.trim();
    const children = (node?.children ?? []).filter((c) => !isWhitespaceText(c));
    const first = children[0];
    const restIsOnlyNestedList = children.length === 1
        || (children.length === 2 && (children[1]?.tagName === 'ul' || children[1]?.tagName === 'ol'));
    const isBareStrong = first?.tagName === 'strong' && restIsOnlyNestedList;
    const isParagraphWrappedStrong = first?.tagName === 'p'
        && (first.children ?? []).filter((c) => !isWhitespaceText(c)).length === 1
        && first.children.find((c) => !isWhitespaceText(c))?.tagName === 'strong'
        && restIsOnlyNestedList;
    return isBareStrong || isParagraphWrappedStrong;
}
